import { isClickedOutside, onDocumentLoaded } from '@theme/utilities';

/**
 * Skip links: focus the hash target and scroll with scroll-padding applied.
 * Native hash navigation can leave #MainContent top edge under sticky header chrome.
 *
 * @param {Event} event
 */
function handleSkipLinkClick(event) {
  const link = event.currentTarget;
  if (!(link instanceof HTMLAnchorElement)) return;

  const hash = link.getAttribute('href');
  if (!hash?.startsWith('#')) return;

  const target = document.getElementById(hash.slice(1));
  if (!(target instanceof HTMLElement)) return;

  event.preventDefault();
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'start' });
}

/**
 * Bottom edge of header chrome currently visible at the top of the viewport.
 * When the announcement bar has scrolled away, only the sticky header counts.
 *
 * @returns {number | null}
 */
function getVisibleHeaderGroupBottom() {
  const headerGroup = document.querySelector('#header-group');
  if (!headerGroup) return null;

  let bottom = 0;
  for (const child of headerGroup.children) {
    if (!(child instanceof HTMLElement)) continue;

    const { bottom: childBottom } = child.getBoundingClientRect();
    if (childBottom > 0) {
      bottom = Math.max(bottom, childBottom);
    }
  }

  return bottom > 0 ? Math.round(bottom) : null;
}

function updateSearchModalTop() {
  const bottom = getVisibleHeaderGroupBottom();
  if (bottom === null) return;

  document.body.style.setProperty('--search-modal-top', `${bottom}px`);
}

onDocumentLoaded(() => {
  for (const link of document.querySelectorAll('.skip-to-content-link[href^="#"]')) {
    link.addEventListener('click', handleSkipLinkClick);
  }

  const searchModal = document.getElementById('search-modal');
  if (!searchModal || typeof searchModal.showDialog !== 'function') return;

  const dialog = searchModal.querySelector('dialog');
  const searchInput = searchModal.querySelector('.search-input');

  /** @param {MouseEvent} event */
  const handleSearchOutsideClick = (event) => {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    if (!isClickedOutside(event, dialog)) return;

    searchModal.closeDialog();
  };

  const getSearchTrigger = () =>
    searchModal.ownerDocument.querySelector('search-button [aria-controls="search-modal"]');

  const syncSearchTriggerExpanded = (expanded) => {
    const trigger = getSearchTrigger();
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  };

  const syncSearchTriggerAccessibleName = () => {
    const trigger = getSearchTrigger();
    const searchButton = trigger?.closest('search-button');
    if (!(trigger instanceof HTMLElement) || !(searchButton instanceof HTMLElement)) return;

    const isTextDesktop =
      searchButton.classList.contains('search-action--text') &&
      window.matchMedia('(min-width: 750px)').matches;
    const label = trigger.dataset.searchLabel ?? 'Search';

    if (isTextDesktop) {
      trigger.removeAttribute('aria-label');
      return;
    }

    trigger.setAttribute('aria-label', label);
  };

  syncSearchTriggerAccessibleName();
  window.addEventListener('resize', syncSearchTriggerAccessibleName);

  searchModal.addEventListener('dialog:open', () => {
    syncSearchTriggerExpanded(true);
    document.addEventListener('click', handleSearchOutsideClick, true);

    requestAnimationFrame(() => {
      updateSearchModalTop();
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus({ preventScroll: true });
      }
    });
  });

  searchModal.addEventListener('dialog:close', () => {
    document.removeEventListener('click', handleSearchOutsideClick, true);
    syncSearchTriggerExpanded(false);
  });

  const showDialog = searchModal.showDialog.bind(searchModal);

  searchModal.showDialog = () => {
    updateSearchModalTop();
    showDialog();
  };
});

